import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import {RESPONSIVE_SCREEN_WIDTH} from '../../shared/constants/responsive.constants';

/**
 * Options for configuring viewport intersection observation
 */
export interface ViewportIntersectionOptions {
  /**
   * Margin around the viewport for early/late intersection detection.
   * Can be a single value (e.g., '50px') or separate values (e.g., '50px 0px').
   * If not provided, uses responsive default (2000px on mobile, 300px on desktop).
   */
  rootMargin?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ViewportIntersectionService implements OnDestroy {
  // Map of observers by rootMargin config (e.g., 'default', '0px', '50px')
  private observers = new Map<string, IntersectionObserver>();
  // Map element to its visibility subject
  private visibilitySubjects = new Map<Element, BehaviorSubject<boolean>>();

  constructor() {
    this.initializeDefaultObserver();
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Observe an element for viewport intersection
   * @param element The DOM element to observe
   * @param options Optional configuration for intersection detection
   * @returns Observable that emits true when element is visible (including buffer), false otherwise
   */
  observe$(element: Element, options?: ViewportIntersectionOptions): Observable<boolean> {
    const observer = this.getOrCreateObserver(options?.rootMargin);

    // If IntersectionObserver is not supported, always return true (visible)
    if (!observer) {
      if (!this.visibilitySubjects.has(element)) {
        this.visibilitySubjects.set(element, new BehaviorSubject<boolean>(true));
      }
      return this.visibilitySubjects.get(element)!.asObservable();
    }

    if (this.visibilitySubjects.has(element)) {
      // Already observing this element, return existing subject
      return this.visibilitySubjects.get(element)!.asObservable();
    }

    // Create new subject for this element
    this.visibilitySubjects.set(element, new BehaviorSubject<boolean>(false));

    // Start observing - IntersectionObserver will handle initial state
    observer.observe(element);

    return this.visibilitySubjects.get(element)!.asObservable();
  }

  /**
   * Stop observing an element
   * @param element The DOM element to stop observing
   */
  unobserve(element: Element): void {
    const subject = this.visibilitySubjects.get(element);
    if (subject) {
      // Try to unobserve from all observers (element will only be in one)
      this.observers.forEach(observer => observer.unobserve(element));

      subject.complete();
      this.visibilitySubjects.delete(element);
    }
  }


  /**
   * Disconnect all observers and clean up
   */
  disconnect(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();

    // Complete all subjects
    this.visibilitySubjects.forEach(subject => subject.complete());
    this.visibilitySubjects.clear();
  }

  private getOrCreateObserver(rootMargin?: string): IntersectionObserver | null {
    const key = rootMargin || 'default';

    if (this.observers.has(key)) {
      return this.observers.get(key)!;
    }

    if (!('IntersectionObserver' in window)) {
      return null;
    }

    // Create custom observer for non-default rootMargin
    const observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: rootMargin!,
        threshold: 0.0
      }
    );
    this.observers.set(key, observer);
    return observer;
  }

  private initializeDefaultObserver(): void {
    if ('IntersectionObserver' in window) {
      // Use larger buffer on small viewports since they typically show fewer tiles
      // and benefit from aggressive preloading during fast scrolling
      const isSmallViewport = window.innerWidth <= RESPONSIVE_SCREEN_WIDTH;
      const rootMargin = isSmallViewport ? '2000px' : '300px'; // Much larger buffer for small viewports

      const observer = new IntersectionObserver(
        (entries) => this.handleIntersection(entries),
        {
          root: null, // viewport
          rootMargin: rootMargin, // Dynamic buffer based on platform
          threshold: 0.0
        }
      );
      this.observers.set('default', observer);
    } else {
      console.warn('IntersectionObserver not supported. Tiles will load values regardless of viewport visibility.');
    }
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      const subject = this.visibilitySubjects.get(entry.target);
      if (subject) {
        subject.next(entry.isIntersecting);
      }
    });
  }
}
